import { FunctionComponent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { fetchGet, toQueryString } from "../../../../service/api_client";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { formatAmountWithCommas } from "../../../../ui/pages/content_panel/utils/helpers";
import { urls } from "../../../../utils/urls";
import { Project } from "../../models/project_model";

interface LinkedProjectsTableProps {
  costCenterId: string;
  showTitle?: boolean;
}

/** Project Master entries linked to a cost center (CC list expand + CC view). */
const LinkedProjectsTable: FunctionComponent<LinkedProjectsTableProps> = ({
  costCenterId,
  showTitle = true,
}) => {
  const navigation = useHistory();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    if (!costCenterId) return;
    let active = true;
    setLoading(true);
    fetchGet(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/project` +
        toQueryString({ cost_center_id: costCenterId, all: true }),
    )
      .then((res) => {
        if (!active) return;
        // A backend without the cost_center_id filter returns every project,
        // so keep only the ones actually linked to this cost center.
        if (res.success)
          setProjects(
            ((res.data?.data ?? []) as Project[]).filter(
              (p) => p.cost_center_id === costCenterId,
            ),
          );
        else showToast({ type: ToastType.error, text: res.error });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterId]);

  return (
    <div id={`linked_projects_${costCenterId}`} className="text-sm text-left">
      {showTitle && (
        <h3 className="font-semibold mb-2">
          Linked Projects {!loading && `(${projects.length})`}
        </h3>
      )}
      {loading ? (
        <div className="text-gray-500 text-xs">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="text-gray-500 text-xs">
          No projects linked to this cost center.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white border text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Project ID</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-right">Total Budget</th>
                <th className="px-3 py-2 text-right">Spent</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id} className="border-t">
                  <td
                    className="px-3 py-2 whitespace-nowrap text-blue-600 hover:underline cursor-pointer"
                    onClick={() =>
                      navigation.push(
                        urls.procurement_project +
                          "/view-project/" +
                          project.id,
                      )
                    }
                  >
                    {project.id}
                  </td>
                  <td className="px-3 py-2">{project.name}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {formatAmountWithCommas(project.totalBudget) ?? "--"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {formatAmountWithCommas(project.spent) ?? "--"}
                  </td>
                  <td className="px-3 py-2">{project.projectStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LinkedProjectsTable;
